import React, { useEffect, useRef } from 'react';
import type { User } from '../../types/config';
import {
  ADSENSE_CLIENT_ID,
  ADSENSE_REQUEST_NPA,
  canShowStudentAds,
  isAdSenseConfigured,
  studentAdSlots,
} from '../../config/adsense';

type AdsByGoogle = Array<Record<string, unknown>> & {
  requestNonPersonalizedAds?: number;
};

declare global {
  interface Window {
    adsbygoogle?: AdsByGoogle;
  }
}

const SCRIPT_ID = 'google-adsense-script';

function ensureAdSenseScript(): void {
  if (typeof document === 'undefined' || !ADSENSE_CLIENT_ID) return;

  if (ADSENSE_REQUEST_NPA) {
    const queue = (window.adsbygoogle = window.adsbygoogle || []) as AdsByGoogle;
    queue.requestNonPersonalizedAds = 1;
  }

  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(
    ADSENSE_CLIENT_ID
  )}`;
  document.head.appendChild(script);
}

function AdUnit({ slot }: { slot: string }) {
  const pushed = useRef(false);

  useEffect(() => {
    if (pushed.current) return;
    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // AdSense may throw if blocked or not ready; ignore.
    }
  }, [slot]);

  return (
    <ins
      className="adsbygoogle"
      style={{ display: 'block', minHeight: 90 }}
      data-ad-client={ADSENSE_CLIENT_ID}
      data-ad-slot={slot}
      data-ad-format="horizontal"
      data-full-width-responsive="true"
    />
  );
}

/**
 * Bottom-of-page ads for student portal users only.
 * Renders at most two units; no-op when AdSense env is unset or user is staff.
 */
export function StudentPageAds({ user }: { user: User }) {
  const enabled = canShowStudentAds(user) && isAdSenseConfigured();
  const slots = enabled ? studentAdSlots() : [];

  useEffect(() => {
    if (!enabled || slots.length === 0) return;
    ensureAdSenseScript();
  }, [enabled, slots.length]);

  if (!enabled || slots.length === 0) return null;

  return (
    <aside
      className="mt-auto shrink-0 border-t border-secondary-200/80 bg-secondary-50/80 px-4 py-3"
      aria-label="Sponsored"
    >
      <p className="mb-2 text-center text-[10px] uppercase tracking-wide text-secondary-400">
        Sponsored
      </p>
      <div className="mx-auto flex max-w-4xl flex-col gap-3">
        {slots.map((slot) => (
          <div key={slot} className="w-full overflow-hidden">
            <AdUnit slot={slot} />
          </div>
        ))}
      </div>
    </aside>
  );
}
